import { sanitizeHtml } from "utils/sanitizeHtml";
import Logo from "designs/DesignB/components/Logo/Logo";
import { FooterLink } from "data/types";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useData } from "data/data-provider";

export interface WidgetFooterMenu {
  id: string;
  title: string;
  menus: FooterLink[];
}

const isAbsoluteUrl = (s?: string) => !!s && /^https?:\/\//i.test(s);
const isMailOrTel = (s?: string) =>
  !!s && (s.startsWith("mailto:") || s.startsWith("tel:"));

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const { companyDetails, contactItems, footerItems, address, socialItems, footer } =
    useData();

  // Convention used by the website-builder: footerItems with a `heading` are
  // labelled links; ones without are plain info lines under the brand block.
  const links = (footerItems || []).filter((item: any) => item?.heading);
  const infoLines = (footerItems || []).filter((item: any) => !item?.heading);

  const currentYear = new Date().getFullYear();
  const companyName: string =
    companyDetails?.company_name?.trim() ||
    companyDetails?.company?.name?.trim() ||
    "";

  // 1NOW layout variant chosen in the FMS (Section.layout_variant):
  //   "columns"  → 12-col grid: brand + Quick Links + Contact (default)
  //   "centered" → minimal centered single column: logo + socials + tagline
  // Default "columns" preserves the previous render for tenants who haven't
  // picked a variant.
  const layoutVariant: "columns" | "centered" =
    footer?.layout_variant === "centered" ? "centered" : "columns";

  return (
    <footer className="bg-footer-color text-footer-text-color">
      {/* Top accent bar */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-primary-200/60 to-transparent" />

      {/* Plain `container` — match the Header logo and every home-page
          section above. Earlier this added `px-4` on top of the container,
          which forced 16px padding at 2xl (px-4 wins over container's
          128px), making the footer brand block / link columns sit further
          left than the logo + banner content. */}
      <div className="container pt-8 pb-6 sm:pt-10 sm:pb-7">
        {layoutVariant === "columns" && (
        <div className="grid grid-cols-1 gap-7 md:gap-8 md:grid-cols-12">
          {/* Brand block */}
          <div className="md:col-span-5">
            <div className="mb-3">
              <Logo />
            </div>
            {footer?.description && (
              <p
                className="max-w-md text-sm leading-relaxed opacity-80"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(footer.description) }}
              />
            )}

            {/* FMS-driven info lines (e.g. tagline, license, address note) */}
            {infoLines.length > 0 && (
              <div className="mt-3 max-w-md space-y-1 text-sm opacity-80">
                {infoLines.map((item: any, index: number) => (
                  <p
                    key={item.id || index}
                    className="break-words leading-relaxed"
                  >
                    {item.description}
                  </p>
                ))}
              </div>
            )}

            {/* Social row */}
            {socialItems?.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {socialItems.map((s: any, i: number) => {
                  const href = s?.description || "#";
                  const label = s?.heading || "Social";
                  return (
                    <a
                      key={s?.id || i}
                      href={href}
                      target={isMailOrTel(href) ? undefined : "_blank"}
                      rel={isMailOrTel(href) ? undefined : "noopener noreferrer"}
                      aria-label={label}
                      title={label}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-footer-text-color/10 ring-1 ring-footer-text-color/15 transition-all duration-300 hover:bg-primary-200 hover:ring-primary-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary-200/30"
                    >
                      {s?.image_url ? (
                        <img
                          src={s.image_url}
                          alt=""
                          className="h-3.5 w-3.5 object-contain"
                        />
                      ) : (
                        <span className="text-xs font-bold uppercase">
                          {label.charAt(0)}
                        </span>
                      )}
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="md:col-span-3">
            <h3 className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-primary-200">
              Quick Links
            </h3>
            <ul className="list-none space-y-1.5 text-sm">
              {links.length > 0
                ? links.map((item: any, index: number) => {
                    const href = item.description || "#";
                    const isExternal =
                      isAbsoluteUrl(href) || isMailOrTel(href);
                    return (
                      <li key={item.id || index}>
                        {isExternal ? (
                          <a
                            href={href}
                            target={isMailOrTel(href) ? undefined : "_blank"}
                            rel={
                              isMailOrTel(href)
                                ? undefined
                                : "noopener noreferrer"
                            }
                            className="inline-flex items-center gap-1.5 transition-all duration-200 hover:text-primary-200 hover:translate-x-0.5"
                          >
                            <span className="h-px w-3 bg-current opacity-60" />
                            {item.heading}
                          </a>
                        ) : (
                          <button
                            type="button"
                            onClick={() => navigate(href)}
                            className="inline-flex items-center gap-1.5 transition-all duration-200 hover:text-primary-200 hover:translate-x-0.5"
                          >
                            <span className="h-px w-3 bg-current opacity-60" />
                            {item.heading}
                          </button>
                        )}
                      </li>
                    );
                  })
                : ["About Us", "Vehicles", "Contact"].map((label, i) => (
                    <li key={i}>
                      <Link
                        to={`/${label.toLowerCase().replace(" ", "-")}`}
                        className="inline-flex items-center gap-1.5 transition-all duration-200 hover:text-primary-200 hover:translate-x-0.5"
                      >
                        <span className="h-px w-3 bg-current opacity-60" />
                        {label}
                      </Link>
                    </li>
                  ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <h3 className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-primary-200">
              Get in Touch
            </h3>
            <ul className="list-none space-y-2 text-sm">
              {contactItems.map((item: any, index: number) => {
                const href = item.description || "";
                const isExternal = isAbsoluteUrl(href) || isMailOrTel(href);
                // Pick an inline SVG icon based on the link type. Inline
                // SVGs use `currentColor` so they always match the footer's
                // cream text — unlike the FMS-uploaded PNG icons from
                // flaticon, which are baked black and disappear on the dark
                // footer background. The keypoint's image_url is ignored
                // here on purpose; the icon set for phone / email / hours
                // is fixed by the link type.
                const isTel = href.startsWith("tel:");
                const isMail = href.startsWith("mailto:");
                const icon = isTel ? (
                  <svg
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.86 19.86 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.86 19.86 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                ) : isMail ? (
                  <svg
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <path d="M22 6l-10 7L2 6" />
                  </svg>
                ) : (
                  // Fallback (e.g. an "hours" line without a link) — clock.
                  <svg
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                );
                return (
                  <li
                    key={item.id || index}
                    className="flex items-start gap-3 break-words"
                  >
                    {icon}
                    <div className="min-w-0">
                      {item.heading ? (
                        isExternal ? (
                          <a
                            href={href}
                            className="transition-colors duration-200 hover:text-primary-200"
                          >
                            {item.heading}
                          </a>
                        ) : (
                          <span>{item.heading}</span>
                        )
                      ) : (
                        <span className="opacity-80">{item.description}</span>
                      )}
                    </div>
                  </li>
                );
              })}
              {address.map((item: any, index: number) => (
                <li
                  key={item.id || index}
                  className="flex items-start gap-3 break-words leading-relaxed opacity-80"
                >
                  <svg
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span>{item.description}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        )}

        {/* Centered footer — logo + tagline + horizontal nav row + inline
            contact + address + socials, all centered. Follows the popular
            SaaS landing-page footer pattern (Notion / Linear / Vercel) where
            the structural grid is replaced by stacked centered rows but the
            information density is preserved — the older "minimal closing
            strip" version dropped Quick Links / Contact / Address entirely,
            which was the wrong default for a real tenant site. */}
        {layoutVariant === "centered" && (
          <div className="flex flex-col items-center text-center gap-5">
            {/* Logo */}
            <Logo />

            {/* Brand tagline — FMS-driven info lines, else the footer's
                editable description (set in the builder). */}
            {infoLines.length > 0 ? (
              <div className="max-w-xl text-sm leading-relaxed opacity-80 space-y-1">
                {infoLines.map((item: any, index: number) => (
                  <p key={item.id || index}>{item.description}</p>
                ))}
              </div>
            ) : footer?.description ? (
              <p
                className="max-w-xl text-sm leading-relaxed opacity-80"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(footer.description) }}
              />
            ) : null}

            {/* Horizontal nav row — same `links` array the columns layout
                shows as Quick Links, here rendered inline with comfortable
                gap-6 spacing so the row visually reads as a primary nav. */}
            {links.length > 0 && (
              <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm font-medium">
                {links.map((item: any, index: number) => {
                  const href = item.description || "#";
                  const isExternal = isAbsoluteUrl(href) || isMailOrTel(href);
                  const linkClass =
                    "transition-colors duration-200 hover:text-primary-200";
                  return isExternal ? (
                    <a
                      key={item.id || index}
                      href={href}
                      target={isMailOrTel(href) ? undefined : "_blank"}
                      rel={
                        isMailOrTel(href)
                          ? undefined
                          : "noopener noreferrer"
                      }
                      className={linkClass}
                    >
                      {item.heading}
                    </a>
                  ) : (
                    <button
                      key={item.id || index}
                      type="button"
                      onClick={() => navigate(href)}
                      className={linkClass}
                    >
                      {item.heading}
                    </button>
                  );
                })}
              </nav>
            )}

            {/* Inline contact row — phone / email / hours rendered as a
                single centered line with the same SVG icons the columns
                layout uses (inline-styled `currentColor` so they read on
                the dark footer regardless of the FMS-uploaded PNG icon). */}
            {contactItems.length > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm">
                {contactItems.map((item: any, index: number) => {
                  const href = item.description || "";
                  const isExternal =
                    isAbsoluteUrl(href) || isMailOrTel(href);
                  const isTel = href.startsWith("tel:");
                  const isMail = href.startsWith("mailto:");
                  const icon = isTel ? (
                    <svg
                      aria-hidden="true"
                      className="h-4 w-4 shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.86 19.86 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.86 19.86 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  ) : isMail ? (
                    <svg
                      aria-hidden="true"
                      className="h-4 w-4 shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <path d="M22 6l-10 7L2 6" />
                    </svg>
                  ) : (
                    <svg
                      aria-hidden="true"
                      className="h-4 w-4 shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  );
                  const label = item.heading || item.description;
                  return (
                    <span
                      key={item.id || index}
                      className="inline-flex items-center gap-2 opacity-90"
                    >
                      {icon}
                      {item.heading && isExternal ? (
                        <a
                          href={href}
                          className="transition-colors duration-200 hover:text-primary-200"
                        >
                          {item.heading}
                        </a>
                      ) : (
                        <span>{label}</span>
                      )}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Centered address row(s) — same map-pin icon the columns
                layout shows, but inline so the row stays short. */}
            {address.length > 0 && (
              <div className="text-sm opacity-80 space-y-1">
                {address.map((item: any, index: number) => (
                  <p
                    key={item.id || index}
                    className="inline-flex items-center justify-center gap-2"
                  >
                    <svg
                      aria-hidden="true"
                      className="h-4 w-4 shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <span>{item.description}</span>
                  </p>
                ))}
              </div>
            )}

            {/* Social row — same look as the columns layout, just centered. */}
            {socialItems?.length > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-2">
                {socialItems.map((s: any, i: number) => {
                  const href = s?.description || "#";
                  const label = s?.heading || "Social";
                  return (
                    <a
                      key={s?.id || i}
                      href={href}
                      target={isMailOrTel(href) ? undefined : "_blank"}
                      rel={isMailOrTel(href) ? undefined : "noopener noreferrer"}
                      aria-label={label}
                      title={label}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-footer-text-color/10 ring-1 ring-footer-text-color/15 transition-all duration-300 hover:bg-primary-200 hover:ring-primary-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary-200/30"
                    >
                      {s?.image_url ? (
                        <img
                          src={s.image_url}
                          alt=""
                          className="h-3.5 w-3.5 object-contain"
                        />
                      ) : (
                        <span className="text-xs font-bold uppercase">
                          {label.charAt(0)}
                        </span>
                      )}
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* 1NOW-10372: legal links are not FMS-driven — every tenant site has
            to expose a privacy policy and terms URL (SMS carriers require one
            before approving a campaign), so this bottom bar renders on both
            layout variants regardless of how the footer is configured.
            Copyright left, links right on the columns layout (they read as
            unanchored on their own); both centered on the centered layout. */}
        <div className="mt-7 border-t border-footer-text-color/15 pt-5 text-sm">
          <div
            className={`flex flex-col-reverse items-center gap-3 ${
              layoutVariant === "centered" ? "" : "sm:flex-row sm:justify-between"
            }`}
          >
            {companyName && (
              <p className="opacity-70">
                © {currentYear} {companyName}. All rights reserved.
              </p>
            )}
            <div
              className={`flex flex-wrap items-center justify-center gap-x-6 gap-y-2 ${
                layoutVariant === "centered" ? "" : "sm:ml-auto"
              }`}
            >
              <Link
                to="/privacy-policy"
                className="transition-colors duration-200 hover:text-primary-200"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-and-conditions"
                className="transition-colors duration-200 hover:text-primary-200"
              >
                Terms &amp; Conditions
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
